
import React, { useState } from 'react';
import { Calendar, Book } from 'lucide-react';
import { BlogPost } from './types';

interface BlogListProps {
  blogPosts: BlogPost[];
  onPostClick: (post: BlogPost) => void;
}

const BlogList: React.FC<BlogListProps> = ({ blogPosts = [], onPostClick }) => {
  const [filter, setFilter] = useState<string | null>(null);

  // Ensure blogPosts is an array
  const safeBlogPosts = Array.isArray(blogPosts) ? blogPosts : [];

  // Get unique categories from valid blog posts
  const categories = safeBlogPosts.length > 0 
    ? Array.from(new Set(safeBlogPosts.map(post => post.category).filter(Boolean)))
    : [];

  // Filter posts
  const filteredPosts = filter
    ? safeBlogPosts.filter(post => post.category === filter)
    : safeBlogPosts;

  return (
    <div className="space-y-8">
      {/* Category filters - only show if we have categories */}
      {categories.length > 0 && (
        <div className="mb-8 flex flex-wrap gap-2 justify-center">
          <button
            onClick={() => setFilter(null)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              filter === null
                ? 'bg-primary text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
            aria-label="Show all blog posts"
          >
            All Topics
          </button>
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setFilter(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                filter === category
                  ? 'bg-primary text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              aria-label={`Filter by ${category}`}
            >
              {category}
            </button>
          ))}
        </div>
      )}

      {/* Blog posts grid */}
      {filteredPosts.length === 0 ? (
        <div className="text-center py-12">
          <h3 className="text-xl font-medium mb-2 text-gray-800">No posts found</h3>
          <p className="text-gray-600">Try selecting a different category or clear your filters</p>
          {filter !== null && (
            <button
              onClick={() => setFilter(null)}
              className="mt-4 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
              aria-label="Show all blog posts"
            >
              Show All Posts
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredPosts.map(post => (
            <div
              key={post.id}
              className="bg-white rounded-xl shadow-md overflow-hidden transition-all hover:shadow-lg animate-fadeIn"
            >
              <div className="relative overflow-hidden h-56">
                <img
                  src={post.image || '/placeholder.svg'}
                  alt={post.title}
                  className="w-full h-full object-cover transition-transform hover:scale-105 duration-700"
                  loading="lazy"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/placeholder.svg';
                    target.onerror = null;
                  }}
                />
                <div className="absolute top-0 left-0 m-4">
                  <span className="bg-primary/90 text-white px-2 py-1 rounded text-xs font-medium">
                    {post.category}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full text-xs font-medium flex items-center">
                    <Calendar size={12} className="mr-1" />
                    {post.date}
                  </span>
                  <span className="bg-amber-100 text-amber-800 px-2 py-1 rounded-full text-xs font-medium flex items-center">
                    <Book size={12} className="mr-1" />
                    {post.author}
                  </span>
                </div>
                <h3 className="text-xl font-semibold mb-2 line-clamp-2 text-gray-800 hover:text-primary transition-colors">
                  {post.title}
                </h3>
                <p className="text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>
                <button
                  onClick={() => onPostClick(post)}
                  className="text-primary hover:text-primary-dark font-medium text-sm inline-flex items-center group"
                  aria-label={`Read more about ${post.title}`}
                >
                  Read More
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-1"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Browse by topic CTA */}
      {filter === null && categories.length > 1 && filteredPosts.length > 3 && (
        <div className="pt-8 text-center">
          <h3 className="text-xl font-semibold mb-4 text-gray-800">Browse by Topic</h3>
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setFilter(category)}
                className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded-full transition-all"
                aria-label={`Filter by ${category}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogList;
